import type { ComponentPropsWithoutRef } from 'react'
import { useSnapshot } from 'valtio'
import IconRemove from '@/components/Icons/Close'
import IconTag from '@/components/Icons/Tag'
import { state } from '@/lib/state'
import { cn } from '@/lib/utils'
import styles from './Tags.module.css'

type TagProps = {
  tag: string
  isActive?: boolean
  onClick?: (tag: string | null) => void
} & Omit<ComponentPropsWithoutRef<'button'>, 'onClick'>

export function Tag({ tag, isActive, className, onClick, ...props }: TagProps) {
  return (
    <button
      {...props}
      type="button"
      className={cn(styles.tag, className, isActive && styles.active)}
      onClick={() => onClick?.(isActive ? null : tag)}
      data-testid={`tag-${tag}`}
    >
      {isActive ? (
        <IconRemove className={styles.icon} />
      ) : (
        <IconTag className={styles.icon} />
      )}
      <span className={styles.label}>{tag}</span>
    </button>
  )
}

interface TagsProps {
  tags: string[]
  activeTag?: string
  className?: string
  hideLabel?: boolean
  onTagClick?: (tag: string | null) => void
}

export function Tags({
  tags,
  activeTag,
  className,
  hideLabel,
  onTagClick
}: TagsProps) {
  const { radarConfig } = useSnapshot(state)
  const labels = radarConfig?.labels || {}
  const labelText = labels.filterByTag

  return (
    <div className={cn(styles.tags, className)} data-testid="tags-container">
      {!hideLabel && (
        <div className="py-5">{labelText && <h3>{labelText}</h3>}</div>
      )}
      {tags.map(tag => (
        <Tag
          key={tag}
          tag={tag}
          isActive={activeTag === tag}
          onClick={onTagClick}
        />
      ))}
    </div>
  )
}
